import * as THREE from 'three';
import { worldToCell } from '@somnhold/sim';
import { WORLD_PER_HEX } from './coords.ts';

/**
 * Snow for the Spine's north face (engine/spine.ts): the ground as one snowfield instead of hex tiles, the snow that
 * falls, and the sky over it. All in shaders; nothing per flake or per frame on the CPU but a few uniforms.
 *
 * - `snowGround`: the board's (and the outskirts') own material, its colour and its relief painted from the world
 *   position, not from the tiles: long soft swells, drifts combed by the wind, fine wind ripples, cold blue in the
 *   hollows and the rock showing through where the snow is thin (more of it past the board). The relief is only in
 *   the normal (lighting), never in the geometry: the feet stay on the sim's ground.
 * - `smoothTiles`: closes the tiles' grout, drops the sides between tiles of the same height and lays the low steps
 *   out as ramps, so no seams are left to see (the grout ring was a darker, lower ring round every tile: a floor of tiles on snow).
 * - `SnowFall`: points in a box that follows the camera, moved by the clock in the vertex shader (one draw call).
 * - `SkyDome`: a cold gradient with high cloud and a pale sun, round the camera, out of the fog.
 */

const NOISE = /* glsl */ `
float snH(vec2 p) { p = fract(p * vec2(0.1031, 0.1030)); p += dot(p, p.yx + 33.33); return fract((p.x + p.y) * p.x); }
// Value noise with its derivative (iq): x = value in 0..1, yz = d/dx, d/dy.
vec3 snN(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  vec2 du = 6.0 * f * (1.0 - f);
  float a = snH(i), b = snH(i + vec2(1.0, 0.0)), c = snH(i + vec2(0.0, 1.0)), d = snH(i + vec2(1.0, 1.0));
  return vec3(a + (b - a) * u.x + (c - a) * u.y + (a - b - c + d) * u.x * u.y,
              du * (vec2(b - a, c - a) + (a - b - c + d) * u.yx));
}
`;

export interface SnowLook {
  snow: THREE.ColorRepresentation;
  shade: THREE.ColorRepresentation;
  rock: THREE.ColorRepresentation;
}

/** One set of uniforms per Spine place, shared by its ground materials. */
export const snowUniforms = (look: SnowLook, boardR: number, wind: THREE.Vector2): Record<string, THREE.IUniform> => ({
  uSnow: { value: new THREE.Color(look.snow) },
  uShade: { value: new THREE.Color(look.shade) },
  uRock: { value: new THREE.Color(look.rock) },
  uBoardR: { value: boardR },
  uWind: { value: wind.clone().normalize() },
  // How much rock shows on the board and past it (0: none, 1: much).
  uRockIn: { value: 0.25 },
  uRockOut: { value: 0.7 },
});

/** The snowfield's material, from the board's own (vertex colours, flat shading kept for everything else). */
export const snowGround = (src: THREE.MeshStandardMaterial, uniforms: Record<string, THREE.IUniform>, out: boolean): THREE.MeshStandardMaterial => {
  const m = src.clone();
  // Smooth: the tiles' normals are the faces' own (non-indexed), the low steps laid out as ramps (smoothTiles), the
  // outskirts' relief rounded; the swells and drifts come from the shader.
  m.flatShading = false;
  m.roughness = 0.9;
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vSnW;\nvarying float vSnUp;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvSnW = (modelMatrix * vec4(transformed, 1.0)).xyz;\nvSnUp = normalize(mat3(modelMatrix) * objectNormal).y;');
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
varying vec3 vSnW;
varying float vSnUp;
uniform vec3 uSnow;
uniform vec3 uShade;
uniform vec3 uRock;
uniform float uBoardR;
uniform vec2 uWind;
uniform float uRockIn;
uniform float uRockOut;
${NOISE}
// The field's height (its value) and slope (yz) at a point: swells, drifts along the wind, ripples across it.
vec3 snField(vec2 p, out float drift, out float rip) {
  vec2 d = uWind;
  vec2 s = vec2(-d.y, d.x);
  vec2 q = vec2(dot(p, d), dot(p, s));
  vec3 sw = snN(p * 0.16 + 3.1);
  sw.yz *= 0.16;
  vec3 dr = snN(q * vec2(0.22, 0.75) + 7.3);
  vec2 drg = dr.y * 0.22 * d + dr.z * 0.75 * s;
  vec3 ri = snN(q * vec2(0.9, 6.0));
  vec2 rig = ri.y * 0.9 * d + ri.z * 6.0 * s;
  drift = dr.x;
  rip = ri.x;
  float h = sw.x * 0.9 + dr.x * 0.55 + ri.x * 0.035;
  vec2 g = sw.yz * 0.9 + drg * 0.55 + rig * 0.035;
  return vec3(h, g);
}
`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
float snDrift, snRip;
vec3 snF = snField(vSnW.xz, snDrift, snRip);
float snDist = length(vSnW.xz);
float snPast = smoothstep(uBoardR - 0.5, uBoardR + 5.0, snDist);
// Hollows cold and blue, crests bright; a faint wind comb.
vec3 snC = mix(uShade, uSnow, smoothstep(0.35, 1.25, snF.x));
snC *= 0.97 + 0.05 * snRip;
// Rock through the thin snow: blotches, a rim of grey snow round them.
vec3 snR = snN(vSnW.xz * 0.34 + 11.0);
// Broken up by finer noise, so they read as rock breaking the crust, never as smooth puddles.
float snFine = snN(vSnW.xz * 2.1 + 5.0).x;
float snRk = snR.x * 0.55 + snDrift * 0.2 + snFine * 0.3;
float snT = mix(0.9 - uRockIn * 0.16, 0.8 - uRockOut * 0.16, snPast);
float snRock = smoothstep(snT, snT + 0.02, snRk);
float snRim = smoothstep(snT - 0.05, snT, snRk) * (1.0 - snRock);
snC = mix(snC, snC * vec3(0.86, 0.9, 0.96), snRim * 0.6);
snC = mix(snC, uRock * (0.7 + 0.6 * snFine), snRock);
// Sides (a terrace, the board's rim) a little darker and bluer than the field on top.
snC = mix(snC * vec3(0.78, 0.82, 0.9), snC, smoothstep(0.3, 0.9, vSnUp));
diffuseColor.rgb = snC;
`,
      )
      .replace(
        '#include <normal_fragment_maps>',
        `#include <normal_fragment_maps>
{
  // The field's slope as a tilt of the up-facing ground's normal (in view space): swells and drifts in the light.
  float snUpK = smoothstep(0.85, 0.98, vSnUp);
  float snK = 1.15 * (1.0 - snRock * 0.5);
  vec3 snNW = normalize(vec3(-snF.y * snK, 1.0, -snF.z * snK));
  vec3 snNV = normalize(mat3(viewMatrix) * snNW);
  normal = normalize(mix(normal, snNV, snUpK));
}
`,
      );
  };
  m.customProgramCacheKey = () => `somnhold-snow-${out ? 'out' : 'board'}`;
  m.needsUpdate = true;
  return m;
};

/**
 * Closes the seams of a hex-tile ground: up-facing triangles (tops and grout rings) to their tile's height, sides
 * between tiles of the same height folded flat (no area, nothing drawn). A side over a real step (a terrace, the
 * board's own edge) stays.
 */
export const smoothTiles = (geo: THREE.BufferGeometry, heights: Float32Array, radius: number, ground?: (x: number, z: number) => number): void => {
  const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
  if (!pos || geo.index) return;
  const K = 1000 / WORLD_PER_HEX;
  const hAt = (x: number, z: number): number | null => {
    const id = worldToCell(radius, Math.round(x * K), Math.round(z * K));
    if (id < 0) return ground ? ground(x, z) : null;
    return heights[id] ?? null;
  };
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const n = new THREE.Vector3();
  const e1 = new THREE.Vector3();
  const e2 = new THREE.Vector3();
  for (let i = 0; i < pos.count; i += 3) {
    a.fromBufferAttribute(pos, i);
    b.fromBufferAttribute(pos, i + 1);
    c.fromBufferAttribute(pos, i + 2);
    n.crossVectors(e1.subVectors(b, a), e2.subVectors(c, a));
    const len = n.length();
    if (len < 1e-9) continue;
    n.multiplyScalar(1 / len);
    const cx = (a.x + b.x + c.x) / 3;
    const cz = (a.z + b.z + c.z) / 3;
    if (Math.abs(n.y) > 0.9) {
      // A top or a grout ring: to the tile's own height.
      const h = hAt(cx, cz);
      if (h === null) continue;
      for (let k = 0; k < 3; k++) pos.setY(i + k, h);
      continue;
    }
    // A side: is the ground just past it as high as its top? Then it folds flat (nothing drawn).
    const top = Math.max(a.y, b.y, c.y);
    const beyond = hAt(cx + n.x * 0.12, cz + n.z * 0.12);
    if (beyond === null) continue;
    if (beyond >= top - 0.02) {
      for (let k = 0; k < 3; k++) pos.setY(i + k, top);
      continue;
    }
    // A low step (a rock hex's tile, the board's rim): its foot laid out over the lower ground as a gentle ramp of
    // snow. Left upright, the thin wall caught the shadows of what stood on it and drew dark lines in the snow.
    const step = top - beyond;
    if (step > 0.45) continue;
    for (let k = 0; k < 3; k++) {
      if (pos.getY(i + k) > top - 0.001) continue;
      pos.setXYZ(i + k, pos.getX(i + k) + n.x * step * 2.5, beyond + 0.004, pos.getZ(i + k) + n.z * step * 2.5);
    }
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  geo.computeBoundingSphere();
};

const FALL_VERT = /* glsl */ `
attribute vec4 aSeed;
uniform float uTime;
uniform float uPx;
uniform vec3 uCenter;
uniform vec3 uBox;
uniform vec2 uWind;
uniform float uSize;
varying float vA;
void main() {
  float sp = 0.75 + aSeed.w * 0.6;
  vec3 p = aSeed.xyz * uBox;
  p.y -= uTime * 0.85 * sp;
  p.xz += uWind * uTime * (1.4 + aSeed.w * 0.8) + vec2(sin(uTime * 1.3 + aSeed.w * 31.0), cos(uTime * 0.9 + aSeed.x * 17.0)) * 0.22;
  vec3 lo = uCenter - uBox * 0.5;
  p = mod(p - lo, uBox) + lo;
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  float d = -mv.z;
  // Fade in and out at the box's top and bottom and very near the lens.
  float y = (p.y - lo.y) / uBox.y;
  vA = smoothstep(0.0, 0.12, y) * smoothstep(1.0, 0.8, y) * smoothstep(0.8, 2.5, d) * (0.55 + 0.45 * aSeed.w);
  gl_PointSize = clamp(uSize * (0.5 + aSeed.w) * uPx / max(0.5, d), 1.0, 9.0);
  gl_Position = projectionMatrix * mv;
}`;

const FALL_FRAG = /* glsl */ `
varying float vA;
void main() {
  vec2 c = gl_PointCoord - 0.5;
  float a = smoothstep(0.5, 0.1, length(c)) * vA;
  if (a < 0.02) discard;
  gl_FragColor = vec4(0.95, 0.97, 1.0, a * 0.85);
}`;

/** Falling snow, light: points in a box that follows what the camera looks at, all moved in the vertex shader. */
export class SnowFall {
  readonly points: THREE.Points;
  private u = {
    uTime: { value: 0 },
    uPx: { value: 400 },
    uCenter: { value: new THREE.Vector3() },
    uBox: { value: new THREE.Vector3(26, 12, 26) },
    uWind: { value: new THREE.Vector2(0.85, 0.35) },
    uSize: { value: 0.07 },
  };
  private fwd = new THREE.Vector3();

  constructor(n: number, wind: THREE.Vector2, strength = 1) {
    const g = new THREE.BufferGeometry();
    const seeds = new Float32Array(n * 4);
    let s = 0x9e3779b9;
    const rnd = (): number => {
      s ^= s << 13;
      s ^= s >>> 17;
      s ^= s << 5;
      return (s >>> 0) / 4294967296;
    };
    for (let i = 0; i < n * 4; i++) seeds[i] = rnd();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    g.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 4));
    this.u.uWind.value.copy(wind).normalize().multiplyScalar(strength);
    this.points = new THREE.Points(g, new THREE.ShaderMaterial({ uniforms: this.u, vertexShader: FALL_VERT, fragmentShader: FALL_FRAG, transparent: true, depthWrite: false }));
    this.points.frustumCulled = false;
    this.points.renderOrder = 9;
    this.points.name = 'spine-snowfall';
    const px = new THREE.Vector2();
    this.points.onBeforeRender = (renderer) => {
      this.u.uPx.value = renderer.getDrawingBufferSize(px).y * 0.5;
    };
  }

  frame(now: number, camera: THREE.Camera | undefined): void {
    this.u.uTime.value = now;
    if (!camera) return;
    // The box round a point some way ahead of the lens: the board's camera and a cinematic's close one alike.
    camera.getWorldDirection(this.fwd);
    const c = this.u.uCenter.value.setFromMatrixPosition(camera.matrixWorld);
    const dist = Math.min(14, Math.max(4, c.y * 0.8));
    c.addScaledVector(this.fwd, dist);
  }

  dispose(): void {
    this.points.geometry.dispose();
    (this.points.material as THREE.Material).dispose();
  }
}

const SKY_VERT = /* glsl */ `
varying vec3 vDir;
void main() {
  vDir = normalize(position);
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  gl_Position.z = gl_Position.w * 0.99999;
}`;

const SKY_FRAG = /* glsl */ `
uniform vec3 uZenith;
uniform vec3 uHorizon;
uniform vec3 uSun;
uniform vec3 uSunDir;
uniform float uTime;
varying vec3 vDir;
${NOISE}
void main() {
  vec3 d = normalize(vDir);
  float y = clamp(d.y, -0.2, 1.0);
  vec3 c = mix(uHorizon, uZenith, pow(smoothstep(-0.02, 0.75, y), 0.7));
  // High streaks of cloud, thinning to the zenith, drifting.
  vec2 p = d.xz / max(0.12, d.y + 0.18) * 1.6 + vec2(uTime * 0.01, 0.0);
  float cl = snN(p * vec2(0.6, 1.8)).x * 0.65 + snN(p * 2.3 + 4.0).x * 0.35;
  float band = smoothstep(0.02, 0.18, y) * smoothstep(0.85, 0.3, y);
  c = mix(c, mix(uHorizon, vec3(1.0), 0.35), smoothstep(0.5, 0.85, cl) * band * 0.5);
  // A pale sun low in the sky and its haze.
  float s = max(0.0, dot(d, normalize(uSunDir)));
  c += uSun * (pow(s, 12.0) * 0.35 + pow(s, 500.0) * 1.2);
  gl_FragColor = vec4(c, 1.0);
  #include <colorspace_fragment>
}`;

/** The sky over the Spine: a big inverted sphere round the camera, drawn behind everything, out of the fog. */
export class SkyDome {
  readonly mesh: THREE.Mesh;
  readonly u = {
    uZenith: { value: new THREE.Color('#5d7491') },
    uHorizon: { value: new THREE.Color('#c3cfdd') },
    uSun: { value: new THREE.Color('#e9f0ff') },
    uSunDir: { value: new THREE.Vector3(-0.6, 0.25, -0.75) },
    uTime: { value: 0 },
  };

  constructor() {
    const mat = new THREE.ShaderMaterial({ uniforms: this.u, vertexShader: SKY_VERT, fragmentShader: SKY_FRAG, side: THREE.BackSide, depthWrite: false, fog: false });
    this.mesh = new THREE.Mesh(new THREE.SphereGeometry(120, 24, 12), mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = -10;
    this.mesh.name = 'spine-sky';
  }

  set(zenith: THREE.ColorRepresentation, horizon: THREE.ColorRepresentation, sun: THREE.ColorRepresentation): void {
    this.u.uZenith.value.set(zenith);
    this.u.uHorizon.value.set(horizon);
    this.u.uSun.value.set(sun);
  }

  frame(now: number, camera: THREE.Camera | undefined): void {
    this.u.uTime.value = now;
    if (camera) this.mesh.position.setFromMatrixPosition(camera.matrixWorld);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
